import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { IndianRupee, ShoppingCart, Users, Truck } from '../../components/icons';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { StatCard } from '../../components/stat-card';
import { Skeleton } from '../../components/skeleton';
import { extractErr } from '../../api/error-message';
import { NAV, isNavItemVisible } from '../../app/layout';
import { Symbol } from '../../components/symbol';
import { PageHeader } from '../../components/page-header';
import { CardHeader } from '../../components/card';
import { ChartEmpty } from '../../components/empty-state';
import { useAuthStore } from '../../store/auth-store';
import { AttentionPanel } from './attention-panel';

// Wired to GET /admin/analytics/dashboard. GMV / revenue are measured on
// DELIVERED order items (money actually earned).

// Chart colours — design system v2: accent bars (kumkum-500), ink-200 grid,
// ink-500 axis text.
const C = { accent: '#D12A66', line: '#E6E0E3', axis: '#7A7177', text: '#1E1A1D' };
const RANGES: { label: string; days: number }[] = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
];

interface Dashboard {
  gmv: number;
  ordersTotal: number;
  ordersToday: number;
  avgOrderValue: number;
  activeSellers: number;
  activeProducts: number;
  customers: number;
  partnersOnline: number;
  ordersByStatus: Record<string, number>;
  topSellers: { sellerId: string; name: string; revenue: number }[];
  revenueTrend: { day: string; revenue: number; items: number }[];
}

const inr = (n: number) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/** `OUT_FOR_DELIVERY` → `Out for delivery`. Display only. */
const pretty = (s: string) => {
  const t = s.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
};

// The analytics endpoint accepts any of these (see analytics.controller.ts).
const REPORT_SCOPES = ['report.sales', 'report.financial', 'report.operations', 'report.delivery'];

/**
 * Landing page. Roles without a report.* scope (CONTENT, SUPPORT, …) would only
 * get a 403 from the analytics endpoint, so they see a welcome panel with links
 * to the sections they can use instead. hasScope is true for SUPER_ADMIN.
 */
export function DashboardPage() {
  const hasScope = useAuthStore((s) => s.hasScope);
  const canSeeAnalytics = REPORT_SCOPES.some((k) => hasScope(k));
  return canSeeAnalytics ? <AnalyticsDashboard /> : <WelcomePanel />;
}

function WelcomePanel() {
  const hasScope = useAuthStore((s) => s.hasScope);
  const hasAnyRole = useAuthStore((s) => s.hasAnyRole);
  const links = NAV.flatMap((s) => s.items).filter(
    (i) => i.href !== '/dashboard' && isNavItemVisible(i, hasScope, hasAnyRole),
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Welcome" description="Jump to a section you have access to." />
      <AttentionPanel />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {links.map((i) => (
          <Link
            key={i.href}
            to={i.href}
            className="panel group flex items-center gap-3 p-[18px] transition-[border-color,box-shadow] duration-micro ease-brand hover:border-surface-line-strong hover:shadow-elev-2"
          >
            <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] bg-admin-soft text-admin-ink">
              <Symbol name={i.icon} size={20} />
            </span>
            <span className="flex-1 text-[14.5px] font-semibold text-ink-900">{i.label}</span>
            <Symbol
              name="arrow_forward"
              size={18}
              className="text-ink-400 transition-colors group-hover:text-admin-ink"
            />
          </Link>
        ))}
      </div>
    </div>
  );
}

function AnalyticsDashboard() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<Dashboard>('/admin/analytics/dashboard', { params: { days } })
      .then(({ data }) => { if (alive) setD(data); })
      .catch((err) => {
        // A 403 means the role lost its report scope since sign-in; the empty
        // state says enough without an error toast on the landing page.
        if ((err as { response?: { status?: number } })?.response?.status === 403) return;
        toast.error(extractErr(err));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const statusData = d ? Object.entries(d.ordersByStatus).map(([status, count]) => ({ status, count })) : [];
  const statusMax = Math.max(1, ...statusData.map((s) => s.count));
  const lastIndex = (d?.revenueTrend.length ?? 0) - 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description={`Marketplace performance for the last ${days} days`}
        actions={
          <div className="seg" role="group" aria-label="Period">
            {RANGES.map((r) => (
              <button key={r.days} type="button" onClick={() => setDays(r.days)} aria-pressed={days === r.days}>
                {r.label}
              </button>
            ))}
          </div>
        }
      />

      {/* What's waiting on this admin — above the numbers, since it's the
          part that needs doing. */}
      <AttentionPanel />

      {loading || !d ? (
        <div role="status" aria-busy="true" aria-label="Loading" className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[124px] !rounded-lg" />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.6fr_1fr]">
            <Skeleton className="h-[320px] !rounded-lg" />
            <Skeleton className="h-[320px] !rounded-lg" />
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="GMV delivered" value={inr(d.gmv)} subValue={`${d.ordersToday} orders today`} icon={<IndianRupee size={20} />} />
            <StatCard label="Orders" value={Number(d.ordersTotal).toLocaleString('en-IN')} subValue={`Avg order ${inr(d.avgOrderValue)}`} icon={<ShoppingCart size={20} />} />
            <StatCard label="Customers" value={Number(d.customers).toLocaleString('en-IN')} subValue={`${d.activeSellers} active sellers`} icon={<Users size={20} />} />
            <StatCard label="Partners online" value={String(d.partnersOnline)} subValue={`${d.activeProducts} live products`} icon={<Truck size={20} />} />
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.6fr_1fr]">
            <div className="panel p-5">
              <CardHeader
                title="Revenue from delivered items"
                actions={d.revenueTrend.length > 0 ? <span className="badge badge-success badge-dot">Live</span> : undefined}
              />
              <div className="h-64">
                {d.revenueTrend.length === 0 ? (
                  <ChartEmpty
                    title={`No delivered orders in the last ${days} days`}
                    body="Revenue appears here once an order is marked delivered."
                    action={<Link to="/orders" className="btn btn-tonal btn-sm">View open orders</Link>}
                  />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={d.revenueTrend} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={C.line} vertical={false} />
                      <XAxis
                        dataKey="day"
                        tick={{ fill: C.axis, fontSize: 12 }}
                        tickFormatter={(v: string) => v.slice(5)}
                        axisLine={{ stroke: C.line }}
                        tickLine={false}
                      />
                      <YAxis tick={{ fill: C.axis, fontSize: 12 }} axisLine={false} tickLine={false} width={56} />
                      <Tooltip
                        cursor={{ fill: '#F6F2F4' }}
                        formatter={(v) => inr(Number(v))}
                        contentStyle={{ borderRadius: 12, border: `1px solid ${C.line}`, fontSize: 13 }}
                      />
                      <Bar dataKey="revenue" radius={[6, 6, 0, 0]} maxBarSize={40}>
                        {/* Latest period at full strength, history at 55% (§04c). */}
                        {d.revenueTrend.map((_, i) => (
                          <Cell key={i} fill={C.accent} fillOpacity={i === lastIndex ? 1 : 0.55} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="panel p-5">
              <CardHeader title="Orders by status" />
              {statusData.length === 0 ? (
                <div className="h-64">
                  <ChartEmpty
                    title="No orders yet"
                    body="Each order's status shows up here as soon as the first one is placed."
                  />
                </div>
              ) : (
                <ul className="space-y-1">
                  {statusData.map((s) => (
                    <li
                      key={s.status}
                      className="grid grid-cols-[minmax(0,130px)_1fr_40px] items-center gap-3 py-1.5 text-[13.5px]"
                    >
                      <span className="truncate text-ink-700">{pretty(s.status)}</span>
                      <span className="h-2.5 overflow-hidden rounded-[5px] bg-surface-2">
                        <i
                          className="block h-full rounded-[5px] bg-admin"
                          style={{ width: `${(s.count / statusMax) * 100}%` }}
                        />
                      </span>
                      <span className="font-tabular text-right font-semibold text-ink-900">{s.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="panel p-5">
            <CardHeader title="Top sellers by revenue" />
            {d.topSellers.length === 0 ? (
              <ChartEmpty
                icon="storefront"
                className="!min-h-[160px]"
                title="No sales yet"
                body={`${d.activeProducts} products are live. Revenue per seller appears once their orders are delivered.`}
              />
            ) : (
              <ol className="-mx-5 -mb-5 divide-y divide-surface-line border-t border-surface-line">
                {d.topSellers.map((s, i) => (
                  <li key={s.sellerId} className="flex items-center gap-3 px-5 py-3.5">
                    <span className="font-tabular grid h-7 w-7 shrink-0 place-items-center rounded-full bg-admin-soft text-[12.5px] font-bold text-admin-ink">
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[14.5px] font-medium text-ink-900">{s.name}</span>
                    <span className="font-tabular font-semibold text-ink-900">{inr(s.revenue)}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      )}
    </div>
  );
}
